import React, { useState, useMemo } from "react";
import { Plus, Trash2, ChevronDown } from "lucide-react";
import {
  ToolShell, Field, Select, Result, fmt,
  SpecCard, SpecValue,
} from "./_shared";
import {
  selectCableSize, selectBreaker, recommendPhase,
  INSTALL_METHODS, ambientFactor,
} from "./_electrical";

/**
 *  PLANT POWER & RUNNING COST
 *  --------------------------------------------------------------
 *  Per item:    daily_kWh = kW × hours_per_day × duty_pct / 100
 *  Plant:       Σ items                            → kWh / day
 *                kWh × days/month                  → kWh / month
 *                kWh × days/year                   → kWh / year
 *  Cost:        kWh × R_per_kWh                    → R / day,month,year
 *  Demand:      avg_kW = daily_kWh / 24
 *                kVA   = avg_kW / power_factor
 *                demand_R = kVA × R_per_kVA_month
 *  CO₂:         annual_kWh × emission_factor       → kg CO₂ / year
 *  --------------------------------------------------------------
 */

const CATEGORIES = [
  { id: "feed",     label: "Feed / source" },
  { id: "pretreat", label: "Pre-treatment" },
  { id: "hp",       label: "High-pressure (RO / UF)" },
  { id: "post",     label: "Post-treatment / polishing" },
  { id: "boost",    label: "Booster / distribution" },
  { id: "transfer", label: "Storage / transfer" },
  { id: "aux",      label: "Auxiliary / panels" },
  { id: "other",    label: "Other" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));

const STARTER_ROWS = [
  { id: 1, name: "Borehole feed pump",         cat: "feed",     kw: 5.5,  hrs: 16, duty: 100 },
  { id: 2, name: "Chemical dosing pump",       cat: "pretreat", kw: 0.18, hrs: 16, duty: 100 },
  { id: 3, name: "High-pressure RO pump",      cat: "hp",       kw: 7.5,  hrs: 14, duty: 100 },
  { id: 4, name: "Clean-water transfer pump",  cat: "transfer", kw: 3.0,  hrs: 8,  duty: 100 },
  { id: 5, name: "Distribution booster",       cat: "boost",    kw: 4.0,  hrs: 18, duty: 80  },
  { id: 6, name: "Control panel & telemetry",  cat: "aux",      kw: 0.4,  hrs: 24, duty: 100 },
];

let nextId = 100;

export default function PlantPowerCalculator() {
  const [rows, setRows] = useState(STARTER_ROWS);

  // Tariff & operational defaults
  const [tariff, setTariff] = useState(2.50);                  // R / kWh
  const [demandTariff, setDemandTariff] = useState(0);          // R / kVA / month — optional
  const [powerFactor, setPowerFactor] = useState(0.85);
  const [daysPerMonth, setDaysPerMonth] = useState(30);
  const [daysPerYear, setDaysPerYear] = useState(365);
  const [co2Factor, setCo2Factor] = useState(0.95);             // kg CO₂ / kWh — Eskom grid avg

  // Electrical sizing
  const [cableRun_m, setCableRun_m] = useState(30);             // distance from main to plant
  const [installMethod, setInstallMethod] = useState("conduit-wall");
  const [ambientC, setAmbientC] = useState(30);
  const [vdMaxPct, setVdMaxPct] = useState(5);

  const updateRow = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = (cat = "other") => {
    nextId += 1;
    setRows((rs) => [...rs, { id: nextId, name: "", cat, kw: 0, hrs: 0, duty: 100 }]);
  };
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));

  // ───────── derived totals ─────────
  const rowsWithKwh = rows.map((r) => ({
    ...r,
    dailyKwh: (r.kw || 0) * (r.hrs || 0) * ((r.duty || 0) / 100),
  }));
  const dailyKwh   = rowsWithKwh.reduce((a, r) => a + r.dailyKwh, 0);
  const monthlyKwh = dailyKwh * daysPerMonth;
  const annualKwh  = dailyKwh * daysPerYear;

  // Peak vs average load
  const peakKw = rows.reduce((a, r) => a + (r.kw || 0), 0);
  const avgKw  = dailyKwh / 24;
  const avgKva = powerFactor > 0 ? avgKw / powerFactor : null;

  // Cost
  const dailyR   = dailyKwh * tariff;
  const monthlyR = monthlyKwh * tariff;
  const annualR  = annualKwh * tariff;
  const monthlyDemandR = (avgKva || 0) * demandTariff;
  const annualDemandR  = monthlyDemandR * 12;
  const totalAnnualR   = annualR + annualDemandR;

  // CO₂
  const annualCo2Kg = annualKwh * co2Factor;

  // ───────── Electrical sizing — based on PEAK connected load ─────────
  const phaseRec = recommendPhase(peakKw);
  const useThreePhase = phaseRec.phase !== "single";
  const sysVoltage = useThreePhase ? 400 : 230;
  const vFactor    = useThreePhase ? Math.sqrt(3) : 1;
  const designCurrent_A = (peakKw * 1000) / (vFactor * sysVoltage * powerFactor);
  const sizingCurrent_A = designCurrent_A * 1.25;   // 25 % cable & breaker margin
  const breaker_A = selectBreaker(sizingCurrent_A);
  const installFactor = INSTALL_METHODS.find((m) => m.id === installMethod)?.factor ?? 1;
  const ambFactor = ambientFactor(ambientC);
  const cable = selectCableSize({
    current_A: sizingCurrent_A,
    length_m: cableRun_m,
    threePhase: useThreePhase,
    voltage: sysVoltage,
    vdMaxPct,
    installFactor,
    ambientFact: ambFactor,
  });

  // Category sub-totals
  const byCategory = useMemo(() => {
    const map = {};
    for (const r of rowsWithKwh) {
      if (!map[r.cat]) map[r.cat] = { count: 0, kw: 0, kwh: 0 };
      map[r.cat].count += 1;
      map[r.cat].kw   += r.kw || 0;
      map[r.cat].kwh  += r.dailyKwh;
    }
    return Object.entries(map)
      .map(([cat, v]) => ({ cat, ...v, share: dailyKwh > 0 ? v.kwh / dailyKwh : 0 }))
      .sort((a, b) => b.kwh - a.kwh);
  }, [rowsWithKwh, dailyKwh]);

  return (
    <ToolShell
      slug="plant-power"
      title="Plant power & running cost"
      oneLiner="Add every motor and panel on site, set its kW and daily hours, and the calculator returns the plant's daily / monthly / annual energy, electricity cost and CO₂ footprint."
      onExport={() => ({
        inputs: [
          { label: "Energy tariff", value: tariff, unit: "R/kWh" },
          { label: "Demand tariff", value: demandTariff, unit: "R/kVA/month" },
          { label: "Power factor", value: powerFactor },
          { label: "Days per month", value: daysPerMonth },
          { label: "Days per year", value: daysPerYear },
          { label: "CO₂ emission factor", value: co2Factor, unit: "kg CO₂/kWh" },
          { label: "── Equipment list ──", value: "" },
          ...rowsWithKwh.map((r) => ({
            label: `${r.name || "(unnamed)"} — ${CAT_LABEL[r.cat]}`,
            value: `${fmt(r.kw)} kW × ${fmt(r.hrs)} h/d × ${fmt(r.duty)} %`,
            unit: `→ ${fmt(r.dailyKwh, 1)} kWh/d`,
          })),
        ],
        results: [
          { label: "Daily energy",   value: fmt(dailyKwh, 1),   unit: "kWh / day" },
          { label: "Monthly energy", value: fmt(monthlyKwh, 0), unit: "kWh / month" },
          { label: "Annual energy",  value: fmt(annualKwh, 0),  unit: "kWh / year" },
          { label: "Daily cost",     value: `R ${fmt(dailyR, 0)}`, unit: "" },
          { label: "Monthly cost",   value: `R ${fmt(monthlyR + monthlyDemandR, 0)}`, unit: "(incl. demand)" },
          { label: "Annual cost",    value: `R ${fmt(totalAnnualR, 0)}`, unit: "(incl. demand)" },
          { label: "Peak connected load",  value: fmt(peakKw, 1), unit: "kW",
            note: "Sum of all installed kW — sized for genset / transformer capacity." },
          { label: "Average load",  value: fmt(avgKw, 2), unit: "kW",
            alts: avgKva !== null ? [{ value: fmt(avgKva, 2), unit: "kVA" }] : null },
          { label: "Annual CO₂",   value: fmt(annualCo2Kg, 0), unit: "kg CO₂ / year",
            alts: [{ value: fmt(annualCo2Kg / 1000, 2), unit: "t CO₂ / year" }] },
          { label: "── Category breakdown ──", value: "" },
          ...byCategory.map((c) => ({
            label: `· ${CAT_LABEL[c.cat]} (${c.count})`,
            value: `${fmt(c.kw, 1)} kW · ${fmt(c.kwh, 1)} kWh/d`,
            unit: `${fmt(c.share * 100, 0)} %`,
          })),
        ],
        notes: [
          "Per item: daily_kWh = kW × hours/day × duty %.",
          "Cost: kWh × R/kWh + (demand_kVA × R/kVA/month) × 12.",
          "CO₂: annual_kWh × 0.95 kg/kWh (South African grid average — adjust if you have a cleaner mix).",
        ],
        spec: rows.length > 0 && dailyKwh > 0 ? {
          title: "Energy & cost specification",
          body: `<strong>${rows.length}</strong> equipment items draw <strong>${fmt(peakKw, 1)} kW</strong> connected, consuming <strong>${fmt(dailyKwh, 1)} kWh/day</strong> (<strong>${fmt(annualKwh, 0)} kWh/year</strong>). At <strong>R ${fmt(tariff, 2)}/kWh</strong> energy + <strong>R ${fmt(demandTariff, 0)}/kVA/month</strong> demand, the plant costs ≈ <strong>R ${fmt(totalAnnualR, 0)}/year</strong>. Annual carbon footprint ≈ <strong>${fmt(annualCo2Kg / 1000, 2)} t CO₂</strong>.`,
        } : null,
      })}
    >
      {/* ───────── Equipment table ───────── */}
      <div
        className="overflow-x-auto"
        style={{ border: "1px solid rgba(12,42,64,0.08)" }}
      >
        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F4F2EC" }}>
              <Th>Equipment name</Th>
              <Th>Category</Th>
              <Th right>kW</Th>
              <Th right>Hrs / day</Th>
              <Th right>Duty %</Th>
              <Th right>kWh / day</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {rowsWithKwh.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-6 text-center"
                style={{ color: "rgba(12,42,64,0.5)", fontSize: "0.9rem" }}>
                No equipment yet — add a row below to begin.
              </td></tr>
            )}
            {rowsWithKwh.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid rgba(12,42,64,0.06)" }}>
                <Td>
                  <input type="text" value={r.name}
                    onChange={(e) => updateRow(r.id, { name: e.target.value })}
                    placeholder="e.g. Borehole feed pump"
                    className="w-full px-2 py-1.5 bg-white border outline-none focus:border-[#1FB6C1]"
                    style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.88rem" }} />
                </Td>
                <Td>
                  <select value={r.cat}
                    onChange={(e) => updateRow(r.id, { cat: e.target.value })}
                    className="w-full px-2 py-1.5 bg-white border outline-none focus:border-[#1FB6C1]"
                    style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.82rem" }}>
                    {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </Td>
                <Td right><NumCell value={r.kw}   onChange={(v) => updateRow(r.id, { kw: v })}   step="0.01" /></Td>
                <Td right><NumCell value={r.hrs}  onChange={(v) => updateRow(r.id, { hrs: v })}  step="0.5" /></Td>
                <Td right><NumCell value={r.duty} onChange={(v) => updateRow(r.id, { duty: v })} step="5" /></Td>
                <Td right>
                  <span className="font-mono" style={{ color: "#0C2A40", fontWeight: 600, fontSize: "0.92rem" }}>
                    {fmt(r.dailyKwh, 1)}
                  </span>
                </Td>
                <Td>
                  <button type="button" onClick={() => removeRow(r.id)}
                    className="inline-flex items-center justify-center transition-colors"
                    style={{ width: 28, height: 28, color: "rgba(12,42,64,0.45)" }}
                    title="Remove"
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#B85C38")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(12,42,64,0.45)")}>
                    <Trash2 size={15} strokeWidth={1.75} />
                  </button>
                </Td>
              </tr>
            ))}
            <tr style={{ borderTop: "1px solid rgba(12,42,64,0.10)", background: "#FCFCFA" }}>
              <Td colSpan="2">
                <button type="button" onClick={() => addRow()}
                  className="inline-flex items-center gap-2 font-mono uppercase px-3 py-1.5 transition-colors"
                  style={{ fontSize: "0.62rem", letterSpacing: "0.18em", color: "#1FB6C1",
                    border: "1px solid rgba(31,182,193,0.35)" }}
                  data-testid="pl-add-row">
                  <Plus size={13} strokeWidth={2} /> Add equipment
                </button>
              </Td>
              <Td right><Strong>{fmt(peakKw, 1)}</Strong></Td>
              <Td />
              <Td />
              <Td right><Strong>{fmt(dailyKwh, 1)}</Strong></Td>
              <Td />
            </tr>
          </tbody>
        </table>
      </div>

      {/* ───────── Tariff & assumptions ───────── */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
        <Field label="Energy tariff" hint="2.50 R/kWh is the SA commercial average for 2026 — adjust to your bill.">
          <SuffixNum value={tariff} onChange={setTariff} suffix="R/kWh" step="0.01" testid="pl-tariff" />
        </Field>
        <Field label="Demand tariff (optional)" hint="Some Eskom tariffs charge for peak kVA — typically 80–250 R/kVA/month.">
          <SuffixNum value={demandTariff} onChange={setDemandTariff} suffix="R/kVA/m" step="1" testid="pl-demand" />
        </Field>
        <Field label="Power factor" hint="0.85 typical for a VSD-heavy plant · 0.92+ with PF correction.">
          <SuffixNum value={powerFactor} onChange={setPowerFactor} suffix="PF" step="0.01" testid="pl-pf" />
        </Field>
        <Field label="Days per month">
          <SuffixNum value={daysPerMonth} onChange={setDaysPerMonth} suffix="days" step="1" testid="pl-dpm" />
        </Field>
        <Field label="Days per year">
          <SuffixNum value={daysPerYear} onChange={setDaysPerYear} suffix="days" step="1" testid="pl-dpy" />
        </Field>
        <Field label="Grid emission factor" hint="0.95 kg CO₂/kWh = current Eskom mix · 0.45 = solar-heavy private grid.">
          <SuffixNum value={co2Factor} onChange={setCo2Factor} suffix="kg CO₂/kWh" step="0.01" testid="pl-co2" />
        </Field>
      </div>

      {/* ───────── Headline totals ───────── */}
      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Result label="Daily energy"
          primary={{ value: fmt(dailyKwh, 1), unit: "kWh / day" }}
          testid="pl-result-daily-kwh" />
        <Result label="Monthly energy"
          primary={{ value: fmt(monthlyKwh, 0), unit: "kWh / month" }}
          testid="pl-result-monthly-kwh" />
        <Result label="Annual energy"
          primary={{ value: fmt(annualKwh, 0), unit: "kWh / year" }}
          alts={[{ value: fmt(annualKwh / 1000, 1), unit: "MWh/year" }]}
          testid="pl-result-annual-kwh" />
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <Result label="Daily cost"
          primary={{ value: `R ${fmt(dailyR, 0)}`, unit: "" }}
          testid="pl-result-daily-cost" />
        <Result label="Monthly cost"
          primary={{ value: `R ${fmt(monthlyR + monthlyDemandR, 0)}`, unit: "" }}
          alts={demandTariff > 0
            ? [{ value: `R ${fmt(monthlyR, 0)} energy + R ${fmt(monthlyDemandR, 0)} demand`, unit: "" }]
            : null}
          testid="pl-result-monthly-cost" />
        <Result label="Annual cost"
          primary={{ value: `R ${fmt(totalAnnualR, 0)}`, unit: "" }}
          alts={demandTariff > 0
            ? [{ value: `incl. R ${fmt(annualDemandR, 0)} demand`, unit: "" }]
            : null}
          testid="pl-result-annual-cost" />
      </div>

      {/* ───────── Sizing & footprint ───────── */}
      <div
        className="mt-5 p-5 md:p-6"
        style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}
      >
        <div className="font-mono uppercase mb-3"
          style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.6)" }}>
          Sizing & footprint
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Result label="Peak connected load"
            primary={{ value: fmt(peakKw, 1), unit: "kW" }}
            note="Sum of installed kW — used to size genset, transformer or grid feed."
            testid="pl-result-peak" />
          <Result label="Average load"
            primary={{ value: fmt(avgKw, 2), unit: "kW" }}
            alts={avgKva !== null ? [{ value: fmt(avgKva, 2), unit: "kVA" }] : null}
            note="What the meter actually sees on average across 24 h."
            testid="pl-result-avg" />
          <Result label="Annual CO₂"
            primary={{ value: fmt(annualCo2Kg, 0), unit: "kg / year" }}
            alts={[{ value: fmt(annualCo2Kg / 1000, 2), unit: "t / year" }]}
            note="At the current grid mix — solar PV displaces this directly."
            testid="pl-result-co2" />
        </div>
      </div>

      {/* ───────── Electrical sizing panel ───────── */}
      <div className="mt-5 p-5 md:p-6"
        style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}>
        <div className="font-mono uppercase mb-4"
          style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.6)" }}>
          Electrical sizing — connection, breaker & cable
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
          <Field label="Cable run length" hint="Distance from the main DB / point of supply to the plant DB.">
            <SuffixNum value={cableRun_m} onChange={setCableRun_m} suffix="m" step="1" testid="pl-cable-len" />
          </Field>
          <Field label="Installation method">
            <Select value={installMethod} onChange={setInstallMethod}
              options={INSTALL_METHODS.map((m) => ({ value: m.id, label: m.label }))}
              testid="pl-install" />
          </Field>
          <Field label="Ambient temperature" hint="Highest sustained ambient at the cable run. Derates above 30 °C.">
            <SuffixNum value={ambientC} onChange={setAmbientC} suffix="°C" step="1" testid="pl-ambient" />
          </Field>
          <Field label="Max voltage drop" hint="SANS 10142 allows ≤ 5 % from main to point of use.">
            <SuffixNum value={vdMaxPct} onChange={setVdMaxPct} suffix="%" step="0.5" testid="pl-vd-max" />
          </Field>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Result label="Recommended phase"
            primary={{
              value: phaseRec.phase === "single" ? "Single" :
                     phaseRec.phase === "three"  ? "Three"  : "Either",
              unit: phaseRec.phase === "single" ? "phase · 230 V"
                  : phaseRec.phase === "three"  ? "phase · 400 V"
                  : "phase",
            }}
            note={phaseRec.note}
            testid="pl-result-phase" />
          <Result label="Design / sizing current"
            primary={{ value: fmt(sizingCurrent_A, 1), unit: "A" }}
            alts={[{ value: fmt(designCurrent_A, 1), unit: `A actual (×1.25 margin)` }]}
            testid="pl-result-current" />
          <Result label="Suggested main breaker"
            primary={{ value: breaker_A ? `${breaker_A}` : "—", unit: "A" }}
            note={breaker_A
              ? `${useThreePhase ? "Three-phase 400 V MCB / MCCB" : "Single-phase 230 V MCB"}.`
              : "Beyond standard MCB range — specify a custom moulded-case breaker."}
            testid="pl-result-breaker" />
          <Result label="Minimum cable size"
            primary={{ value: cable.size ? `${cable.size}` : "—", unit: "mm² copper" }}
            alts={cable.size ? [{ value: fmt(cable.vDrop_pct, 2), unit: `% Vd over ${cableRun_m} m` }] : null}
            status={!cable.size ? "alert"
                    : cable.byAmpacity && cable.byVdrop && cable.byAmpacity !== cable.byVdrop ? "warn"
                    : "ok"}
            note={!cable.size
              ? "Single cable cannot carry this load — split into parallel runs."
              : cable.byAmpacity > cable.byVdrop
              ? `Driven by ampacity, not voltage drop (Vd alone needs only ${cable.byVdrop} mm²).`
              : cable.byVdrop > cable.byAmpacity
              ? `Driven by voltage drop, not ampacity (ampacity alone needs only ${cable.byAmpacity} mm²).`
              : "Meets both ampacity and voltage-drop limits."}
            testid="pl-result-cable" />
        </div>
      </div>

      {/* ───────── Category breakdown ───────── */}
      {byCategory.length > 0 && (
        <div className="mt-5 p-5 md:p-6"
          style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}>
          <div className="font-mono uppercase mb-3"
            style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.6)" }}>
            Category breakdown
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#FFFFFF" }}>
                  <Th>Category</Th>
                  <Th right>Items</Th>
                  <Th right>Installed kW</Th>
                  <Th right>kWh / day</Th>
                  <Th right>Share</Th>
                </tr>
              </thead>
              <tbody>
                {byCategory.map((c) => (
                  <tr key={c.cat} style={{ borderTop: "1px solid rgba(12,42,64,0.06)" }}>
                    <Td><span style={{ color: "#0C2A40", fontWeight: 500 }}>{CAT_LABEL[c.cat]}</span></Td>
                    <Td right>{c.count}</Td>
                    <Td right>{fmt(c.kw, 1)}</Td>
                    <Td right>{fmt(c.kwh, 1)}</Td>
                    <Td right>
                      <span className="font-mono" style={{ color: "#1FB6C1", fontWeight: 600 }}>
                        {fmt(c.share * 100, 0)} %
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows.length > 0 && dailyKwh > 0 && (
        <SpecCard
          eyebrow="Energy & cost specification"
          testid="pl-spec-card"
        >
          <SpecValue>{rows.length}</SpecValue> equipment items draw{" "}
          <SpecValue>{fmt(peakKw, 1)} kW</SpecValue> connected, consuming{" "}
          <SpecValue>{fmt(dailyKwh, 1)} kWh/day</SpecValue> ({fmt(annualKwh, 0)} kWh/year). At{" "}
          <SpecValue>R {fmt(tariff, 2)}/kWh</SpecValue> energy + <SpecValue>R {fmt(demandTariff, 0)}/kVA/month</SpecValue> demand,
          the plant costs ≈ <SpecValue>R {fmt(totalAnnualR, 0)}/year</SpecValue>.
          Annual carbon footprint ≈ <SpecValue>{fmt(annualCo2Kg / 1000, 2)} t CO₂</SpecValue>.
        </SpecCard>
      )}
    </ToolShell>
  );
}

// ───────── small helpers ─────────
function Th({ children, right }) {
  return (
    <th className="px-3 py-2.5 text-left font-mono uppercase"
      style={{
        fontSize: "0.6rem", letterSpacing: "0.2em",
        color: "rgba(12,42,64,0.65)", fontWeight: 600,
        textAlign: right ? "right" : "left",
      }}>
      {children}
    </th>
  );
}
function Td({ children, right, colSpan }) {
  return (
    <td className="px-3 py-2" colSpan={colSpan}
      style={{ textAlign: right ? "right" : "left", verticalAlign: "middle" }}>
      {children}
    </td>
  );
}
function Strong({ children }) {
  return (
    <span className="font-mono"
      style={{ color: "#0C2A40", fontWeight: 700, fontSize: "0.92rem" }}>
      {children}
    </span>
  );
}
function NumCell({ value, onChange, step = "any" }) {
  // Decimal-safe string buffer (used inside the load-list table cells).
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input type="text" inputMode="decimal" value={buf} placeholder="0"
      onChange={(e) => {
        const raw = e.target.value;
        if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
        setBuf(raw);
        if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
        const n = parseFloat(raw.replace(",", "."));
        if (Number.isFinite(n)) onChange(n);
      }}
      className="w-20 px-2 py-1.5 bg-white border outline-none focus:border-[#1FB6C1] text-right"
      style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.88rem", fontWeight: 500 }} />
  );
}
function SuffixNum({ value, onChange, suffix, step = "any", testid }) {
  // Buffered text-mode input — see SuffixNum (PurificationPlantSizing.jsx).
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input type="text" inputMode="decimal" value={buf} placeholder="0"
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
        style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.95rem", fontWeight: 500 }}
        data-testid={testid} />
      <span className="inline-flex items-center px-3 border border-l-0 font-mono"
        style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}>
        {suffix}
      </span>
    </div>
  );
}
